// src/components/DriverNicknameInput
import React, { useEffect, useState, useCallback } from "react";
import "../styles/DriverNicknameInput.css";
import { useTranslation } from "react-i18next";

/**
 * DriverNicknameInput (Modal)
 * Modal para recolher o nickname do motorista e devolver via onConfirm.
 *
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - onConfirm: (nickname: string) => void
 * - initialValue?: string
 * - label?: string              // se vier por prop, tem prioridade sobre o i18n
 * - placeholder?: string        // idem
 * - required?: boolean
 * - disabled?: boolean
 * - maxLength?: number
 * - hint?: string               // se vier por prop, tem prioridade sobre o i18n
 */
export default function DriverNicknameInput({
  isOpen,
  onClose = () => {},
  onConfirm = () => {},
  initialValue = "",
  label,
  placeholder,
  required = false,
  disabled = false,
  maxLength = 120,
  hint,
}) {
  const { t } = useTranslation("driver_nickname_input");

  const [nickname, setNickname] = useState(initialValue || "");
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setNickname(initialValue || "");
      setError("");
    }
  }, [isOpen, initialValue]);

  const effectiveMaxLength = Number(maxLength || 120);
  const remaining = Math.max(0, effectiveMaxLength - (nickname?.length || 0));

  const effectiveLabel = label || t("fields.label");
  const effectivePlaceholder =
    placeholder || t("placeholders.example");
  const effectiveHint =
    hint || t("hint.maxChars", { max: effectiveMaxLength });

  const handleBackdropClick = () => onClose();
  const stop = (e) => e.stopPropagation();

  const submit = useCallback(() => {
    const n = (nickname || "").trim();
    if (!n) {
      // Mantemos o comportamento: nickname é sempre obrigatório
      setError(t("errors.nicknameRequired"));
      return;
    }
    onConfirm(n);
  }, [nickname, onConfirm, t]);

  // ESC para fechar, Enter para confirmar
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Enter") submit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose, submit]);

  if (!isOpen) return null;

  return (
    <div className="nickmodal-backdrop" onClick={handleBackdropClick}>
      <div
        className="nickmodal"
        onClick={stop}
        role="dialog"
        aria-modal="true"
        aria-label={t("aria.modalLabel")}
      >
        <div className="nickmodal__header">
          <h3>{t("title")}</h3>
          <button
            type="button"
            className="nickmodal__close"
            onClick={onClose}
            aria-label={t("buttons.close")}
          >
            ✕
          </button>
        </div>

        <div className="nickmodal__body">
          <div className={`driver-nickname-field ${error ? "has-error" : ""}`}>
            <label className="driver-nickname-label">
              {effectiveLabel}
              {required && (
                <span className="driver-nickname-required"> *</span>
              )}
            </label>

            <input
              type="text"
              className={`driver-nickname-input ${
                error ? "input-error" : ""
              }`}
              value={nickname}
              placeholder={effectivePlaceholder}
              onChange={(e) => {
                setNickname(e.target.value);
                if (error && e.target.value.trim()) setError("");
              }}
              maxLength={effectiveMaxLength}
              disabled={disabled}
              autoComplete="off"
              spellCheck={false}
            />

            <div className="driver-nickname-meta">
              <span className="driver-nickname-hint">{effectiveHint}</span>
              <span className="driver-nickname-count">{remaining}</span>
            </div>

            {error ? (
              <div className="driver-nickname-error">{error}</div>
            ) : null}
          </div>
        </div>

        <div className="nickmodal__footer">
          <button
            type="button"
            className="drivers-btn"
            onClick={onClose}
          >
            {t("buttons.cancel")}
          </button>
          <button
            type="button"
            className="drivers-btn drivers-btn--primary"
            onClick={submit}
          >
            {t("buttons.continue")}
          </button>
        </div>
      </div>
    </div>
  );
}
